// Used only by the four station-operation screens and their report details.
export function createStationOperationsReader({
  intervalMs = 750,
  cacheMs = 60_000,
  getSession = () =>
    typeof localStorage === 'undefined' ? '' : (localStorage.getItem('__TG') ?? ''),
} = {}) {
  const cache = new Map<string, { expiresAt: number; promise: Promise<unknown> }>()
  let session = getSession()
  let tail: Promise<unknown> = Promise.resolve()
  let nextStart = 0

  const invalidate = () => {
    cache.clear()
  }
  const assertSession = (requestSession: string) => {
    if (requestSession !== getSession()) throw new Error('The signed-in session changed.')
  }
  const trimCache = () => {
    for (const [key, entry] of cache) {
      if (cache.size <= 200) break
      // Keep in-flight requests shared, even when the settled cache is full.
      if (entry.expiresAt !== Infinity) cache.delete(key)
    }
  }

  const read = <T>(key: string, load: () => Promise<T>): Promise<T> => {
    const requestSession = getSession()
    if (requestSession !== session) {
      invalidate()
      session = requestSession
    }
    const cached = cache.get(key)
    if (cached && cached.expiresAt > Date.now()) return cached.promise as Promise<T>

    const entry = { expiresAt: Infinity, promise: Promise.resolve() as Promise<unknown> }
    const promise = tail
      .then(async () => {
        const delay = nextStart - Date.now()
        if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay))
        assertSession(requestSession)
        nextStart = Date.now() + intervalMs
        const result = await load()
        assertSession(requestSession)
        entry.expiresAt = Date.now() + cacheMs
        trimCache()
        return result
      })
      .catch((error) => {
        if (cache.get(key) === entry) cache.delete(key)
        throw error
      })
    entry.promise = promise
    cache.set(key, entry)
    // A failed request must not prevent later queued requests from running.
    tail = promise.catch(() => undefined)
    return promise
  }

  return { read, invalidate }
}

export const stationOperationsReader = createStationOperationsReader()
